import React, { useEffect, useRef, useState, Fragment } from 'react';
import {
  Send, FileText, MessageSquare, UploadCloud, Loader2, AlertCircle, RefreshCw,
  Paperclip, X, Plus, Pencil, Trash2, History, ChevronRight,
} from 'lucide-react';
import {
  askAI, getChatHistory, deleteChatHistory,
  reviewDocument, reviewDocumentFull, getReviewJob, listReviewHistory,
  listDocumentTypes, listChecklists, createChecklist, updateChecklist, deleteChecklist,
} from '../services/aiService';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { getEmployees } from '../services/employeeService';

// Same set for every role by design - the backend's per-tool permission
// scoping (app/ai/tools.py) is what actually limits what a given person's
// question can return, so the button list doesn't need to branch by role.
const QUICK_QUESTIONS = [
  'What punch points are overdue in my department?',
  "Summarize this month's delays for my department.",
  'How is my performance this month?',
  'What is the status of a project? (name or ask me which)',
];

// The model (like most chat LLMs) writes plain Markdown - **bold**, "- " bullet
// lines - by default. This is a minimal, dependency-free renderer for just
// those two things, rather than pulling in a full markdown library for a
// single chat bubble's worth of formatting.
const renderInline = (line) =>
  line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );

const MessageContent = ({ text }) => (
  <>
    {text.split('\n').map((line, i) => {
      const bullet = line.match(/^\s*[-*]\s+(.*)/);
      return bullet ? (
        <div key={i} className="flex gap-1.5 pl-1">
          <span>•</span>
          <span>{renderInline(bullet[1])}</span>
        </div>
      ) : (
        <div key={i}>{renderInline(line) || ' '}</div>
      );
    })}
  </>
);

// Ticks up once a second while `active` is true, resets to 0 as soon as
// it isn't - a live "how long has this been running" counter so a wait
// reads as visible progress instead of a frozen spinner. Real response
// times vary a lot here (under a second normally, up to ~20s when the
// automatic rate-limit retry in llm_client.py kicks in) - an elapsed
// counter is honest about that variance in a way a fake progress bar
// with a made-up ETA wouldn't be.
const useElapsedSeconds = (active) => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return undefined;
    }
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return seconds;
};

// Matches app/ai/document_review_job.py's CHUNK_DELAY_SECONDS - the
// per-chunk pacing a full/chunked document review is actually paced at
// (to stay under the AI provider's per-minute rate limit across
// requests), used here so the "time left" shown for a running job is a
// real estimate grounded in that constant, not an arbitrary "~1 min".
const CHUNK_PACE_SECONDS = 65;

const formatChunkEta = (chunksRemaining) => {
  const totalSeconds = Math.max(0, chunksRemaining) * CHUNK_PACE_SECONDS;
  if (totalSeconds < 60) return 'under a minute';
  const minutes = Math.ceil(totalSeconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

const ChatTab = ({ messages, setMessages, attachedDocument, setAttachedDocument, historyLoaded, onClear }) => {
  const user = useAuthStore((s) => s.user);
  const showToast = useUIStore((s) => s.showToast);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const elapsed = useElapsedSeconds(sending);
  const bottomRef = useRef(null);
  // {limit_tokens, remaining_tokens, reset_tokens_seconds} - Groq (and
  // OpenAI-compatible providers generally) sends this on every response,
  // success or failure, so it updates live with zero extra API calls.
  // Only the PER-MINUTE budget - the provider never exposes remaining
  // daily budget as a header, only inside an error once you're already
  // over it (see app/ai/llm_client.py's _parse_quota), so this can warn
  // "this minute is getting tight" but not "today is running low".
  const [quota, setQuota] = useState(null);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || sending) return;

    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      // Only user/assistant text turns go back as history - tool-call
      // plumbing is internal to one request/response cycle, not replayed,
      // local 'error' bubbles aren't a role the provider API knows, and
      // 'hidden' turns (a document's full extracted text, attached from
      // the Document Review tab - see onAskInChat) are included on
      // purpose: they're real history content, just not rendered as a
      // giant wall of text in the thread.
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map(({ role, content }) => ({ role, content }));
      const res = await askAI(question, history);
      setMessages([...nextMessages, { role: 'assistant', content: res.data.answer || '(no answer)' }]);
      if (res.data.quota) setQuota(res.data.quota);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to reach the AI assistant';
      showToast(detail, 'error');
      // A visible, persistent error bubble - not just the toast, which
      // auto-dismisses in 4s and can easily be missed, leaving the chat
      // looking like it silently "isn't replying" rather than having
      // actually failed. The user's own question stays in the thread
      // (it wasn't lost), with a retry affordance for the same question.
      setMessages([...nextMessages, { role: 'error', content: detail, retryText: question }]);
    } finally {
      setSending(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  return (
    <div className="flex flex-col h-[70vh]">
      {(messages.length > 0 || quota) && (
        <div className="flex items-center justify-between px-5 pt-2 gap-3">
          {quota ? (
            <div
              className="flex items-center gap-1.5 text-xs text-gray-400"
              title="Share of the AI provider's per-minute capacity used by the last message - resets continuously, not a daily total."
            >
              <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-1.5 rounded-full ${
                    quota.remaining_tokens / quota.limit_tokens < 0.15 ? 'bg-amber-500' : 'bg-komal-blue'
                  }`}
                  style={{ width: `${Math.max(3, (quota.remaining_tokens / quota.limit_tokens) * 100)}%` }}
                />
              </div>
              AI capacity this minute: {Math.round((quota.remaining_tokens / quota.limit_tokens) * 100)}% left
            </div>
          ) : (
            <span />
          )}
          {messages.length > 0 && (
            <button
              onClick={onClear}
              className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-600 shrink-0"
            >
              <Trash2 size={12} /> Clear conversation
            </button>
          )}
        </div>
      )}

      {attachedDocument && (
        <div className="mx-5 mt-1 flex items-center gap-2 bg-komal-blue/10 text-komal-blue text-xs font-medium px-3 py-1.5 rounded-full w-fit">
          <Paperclip size={12} />
          {attachedDocument}
          <button onClick={() => setAttachedDocument(null)} className="hover:text-komal-blue-dark">
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {historyLoaded && messages.length === 0 && (
          <div className="mb-2">
            <p className="text-sm text-gray-500 mb-3">
              Hi {user?.name?.split(' ')[0] || 'there'} — ask about punch points, delays, performance, or projects.
              Answers are scoped to what your role can normally see.
            </p>
            <div className="flex flex-wrap gap-2">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-xs font-medium bg-komal-blue/10 text-komal-blue hover:bg-komal-blue/20 px-3 py-1.5 rounded-full transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          if (m.hidden) return null; // e.g. a document's raw extracted text - real history, not shown as a bubble
          return m.role === 'error' ? (
            <div key={i} className="flex justify-start">
              <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm bg-red-50 text-red-700 border border-red-200">
                <div className="flex items-center gap-1.5 font-medium">
                  <AlertCircle size={14} /> Couldn't get an answer
                </div>
                <div className="mt-1 text-red-600">{m.content}</div>
                <button
                  onClick={() => send(m.retryText)}
                  className="mt-2 flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline"
                >
                  <RefreshCw size={12} /> Retry
                </button>
              </div>
            </div>
          ) : (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                  m.role === 'user'
                    ? 'bg-komal-blue text-white rounded-br-sm whitespace-pre-wrap'
                    : 'bg-gray-100 text-gray-900 rounded-bl-sm space-y-0.5'
                }`}
              >
                {m.role === 'assistant' ? <MessageContent text={m.content} /> : m.content}
              </div>
            </div>
          );
        })}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Thinking… ({elapsed}s{elapsed >= 10 ? ' — may be briefly rate-limited, still working' : ''})
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form
        className="border-t border-gray-200 p-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={attachedDocument ? `Ask about ${attachedDocument}…` : 'Ask a question…'}
          className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-komal-blue/40"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-40 text-white rounded-full p-2.5"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

const ChecklistEditor = ({ checklist, onClose, onSaved }) => {
  const showToast = useUIStore((s) => s.showToast);
  const [questions, setQuestions] = useState(checklist.questions.join('\n'));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const cleaned = questions.split('\n').map((q) => q.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      showToast('At least one question is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateChecklist(checklist.id, { questions: cleaned });
      showToast('Checklist updated', 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update checklist', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteChecklist(checklist.id);
      showToast('Checklist deleted', 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete checklist', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 mt-2 bg-gray-50">
      <div className="text-xs font-semibold text-gray-600 mb-1.5">Editing: {checklist.name}</div>
      <textarea
        value={questions}
        onChange={(e) => setQuestions(e.target.value)}
        rows={Math.max(4, checklist.questions.length)}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
        placeholder="One question per line"
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={save}
          disabled={saving}
          className="bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded"
        >
          Save
        </button>
        <button onClick={onClose} className="text-xs font-medium text-gray-500 px-3 py-1.5">
          Cancel
        </button>
        <button
          onClick={remove}
          disabled={saving}
          className="ml-auto flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  );
};

const NewChecklistForm = ({ onClose, onSaved }) => {
  const showToast = useUIStore((s) => s.showToast);
  const [documentType, setDocumentType] = useState('');
  const [name, setName] = useState('');
  const [questions, setQuestions] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const cleaned = questions.split('\n').map((q) => q.trim()).filter(Boolean);
    if (!documentType.trim() || !name.trim() || cleaned.length === 0) {
      showToast('Document type, name, and at least one question are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await createChecklist(documentType.trim(), name.trim(), cleaned);
      showToast('Checklist created', 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create checklist', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 mt-2 bg-gray-50 space-y-2">
      <input
        value={documentType}
        onChange={(e) => setDocumentType(e.target.value)}
        placeholder="Document type (e.g. government_tender, purchase_order)"
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Checklist name (e.g. Government Tender - Standard Checklist)"
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      <textarea
        value={questions}
        onChange={(e) => setQuestions(e.target.value)}
        rows={4}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
        placeholder="One question per line"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded"
        >
          Create
        </button>
        <button onClick={onClose} className="text-xs font-medium text-gray-500 px-3 py-1.5">
          Cancel
        </button>
      </div>
    </div>
  );
};

const DocumentReviewTab = ({ onAskInChat }) => {
  const showToast = useUIStore((s) => s.showToast);
  const currentUser = useAuthStore((s) => s.user);
  const isCEO = currentUser?.role === 'CEO';
  const [file, setFile] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const reviewElapsed = useElapsedSeconds(reviewing);
  const [result, setResult] = useState(null);
  const [tooLarge, setTooLarge] = useState(false); // single-request review hit the rate-limit-shaped error - offer the full/chunked path

  const [fullJob, setFullJob] = useState(null); // {id, status, total_chunks, chunks_done, result, error}
  const [startingFull, setStartingFull] = useState(false);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [extraQuestions, setExtraQuestions] = useState('');

  const [editingChecklist, setEditingChecklist] = useState(null);
  const [creatingChecklist, setCreatingChecklist] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openingHistoryId, setOpeningHistoryId] = useState(null);

  // CEO-only: whose history to view. null = the caller's own. The
  // backend (GET /ai/documents/reviews) already enforced this visibility
  // rule from the start (everyone reads their own, only CEO reads
  // someone else's) - this picker was the missing piece, found live when
  // a CEO account correctly couldn't see a subordinate's review that the
  // API could already return.
  const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);

  const refreshHistory = (employeeId = historyEmployeeId) => {
    listReviewHistory(employeeId || undefined)
      .then((res) => setHistory(res.data.reviews))
      .catch(() => {}); // non-fatal - review flow still works without history
  };

  const openHistoryItem = async (id) => {
    setOpeningHistoryId(id);
    try {
      const res = await getReviewJob(id);
      setResult(null);
      setTooLarge(false);
      setFullJob(res.data.job);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load that review', 'error');
    } finally {
      setOpeningHistoryId(null);
    }
  };

  const refreshDocumentTypes = () => {
    listDocumentTypes()
      .then((res) => setDocumentTypes(res.data.document_types))
      .catch(() => {}); // non-fatal - the review flow still works without checklists
  };

  useEffect(() => {
    refreshDocumentTypes();
    refreshHistory();
    if (isCEO) {
      getEmployees()
        .then((res) => setEmployees(res.data.employees || []))
        .catch(() => {}); // non-fatal - CEO just won't see the "view someone else's" picker
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch history whenever the CEO switches whose reviews they're viewing.
  useEffect(() => {
    if (historyEmployeeId === null && !isCEO) return; // nothing to react to for non-CEO
    refreshHistory(historyEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyEmployeeId]);

  useEffect(() => {
    if (!selectedType) {
      setChecklists([]);
      setSelectedChecklistId('');
      return;
    }
    listChecklists(selectedType)
      .then((res) => {
        setChecklists(res.data.checklists);
        setSelectedChecklistId(res.data.checklists[0]?.id || '');
      })
      .catch(() => {});
  }, [selectedType]);

  const refreshChecklists = () => {
    setEditingChecklist(null);
    setCreatingChecklist(false);
    refreshDocumentTypes();
    if (selectedType) {
      listChecklists(selectedType).then((res) => setChecklists(res.data.checklists)).catch(() => {});
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      showToast('Only PDF files are supported', 'error');
      return;
    }
    setFile(f);
    setResult(null);
    setFullJob(null);
    setTooLarge(false);
  };

  const submit = async () => {
    if (!file || reviewing) return;
    setReviewing(true);
    setTooLarge(false);
    try {
      const res = await reviewDocument(file, {
        checklistId: selectedChecklistId || undefined,
        questions: extraQuestions.trim() || undefined,
      });
      setResult(res.data);
      // A new review always belongs to the caller, regardless of whose
      // history a CEO might currently be viewing - switch back to "my
      // reviews" so the fresh one is visible immediately.
      setHistoryEmployeeId(null);
      refreshHistory(null);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to review document';
      showToast(detail, 'error');
      // The backend's friendly rate-limit message (llm_client.py's
      // _friendly_error) always mentions "too large" - detect it here to
      // offer the chunked full-document path instead of a dead end.
      if (detail.toLowerCase().includes('too large')) setTooLarge(true);
    } finally {
      setReviewing(false);
    }
  };

  // Polls a running review-full job every 8s until it's done or failed.
  // Not a fixed-interval setInterval that outlives the component - each
  // poll schedules the next one itself, so it stops cleanly on unmount or
  // once the job finishes, rather than firing into a stale closure.
  useEffect(() => {
    if (!fullJob || fullJob.status === 'done' || fullJob.status === 'failed') return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await getReviewJob(fullJob.id);
        setFullJob(res.data.job);
        if (res.data.job.status === 'done' || res.data.job.status === 'failed') refreshHistory(null);
      } catch {
        // transient poll failure - just try again on the next tick
      }
    }, 8000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullJob]);

  const submitFull = async () => {
    if (!file || startingFull) return;
    setStartingFull(true);
    try {
      const res = await reviewDocumentFull(file, {
        checklistId: selectedChecklistId || undefined,
        questions: extraQuestions.trim() || undefined,
      });
      setFullJob(res.data.job);
      setTooLarge(false);
      setHistoryEmployeeId(null);
      refreshHistory(null);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to start full document review', 'error');
    } finally {
      setStartingFull(false);
    }
  };

  const selectedChecklist = checklists.find((c) => c.id === selectedChecklistId);

  return (
    <div className="p-6 space-y-5">
      {(history.length > 0 || isCEO) && (
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-komal-blue"
            >
              <History size={13} />
              History ({history.length})
              <ChevronRight size={13} className={`transition-transform ${historyOpen ? 'rotate-90' : ''}`} />
            </button>
            {/* CEO-only: whose history to view. The backend
                (GET /ai/documents/reviews) already enforced this - everyone
                reads their own, only CEO can read someone else's - this
                picker was the missing piece, found live when a CEO
                couldn't see a subordinate's review the API could already
                return by id. */}
            {isCEO && employees.length > 0 && historyOpen && (
              <select
                value={historyEmployeeId || ''}
                onChange={(e) => setHistoryEmployeeId(e.target.value ? Number(e.target.value) : null)}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-600"
              >
                <option value="">My reviews</option>
                {employees
                  .filter((e) => e.id !== currentUser.id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>{e.name}'s reviews</option>
                  ))}
              </select>
            )}
          </div>
          {historyOpen && (
            <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
              {history.length === 0 && (
                <div className="px-3 py-4 text-xs text-gray-400 text-center">No reviews yet.</div>
              )}
              {history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => openHistoryItem(h.id)}
                  disabled={openingHistoryId === h.id}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 truncate">{h.filename}</div>
                    <div className="text-gray-400">
                      {new Date(h.created_at).toLocaleString()}
                      {h.checklist_name ? ` · ${h.checklist_name}` : ''}
                      {h.total_chunks > 1 ? ` · ${h.total_chunks} sections` : ''}
                      {h.status === 'failed' ? ' · failed' : h.status !== 'done' ? ` · ${h.status}` : ''}
                    </div>
                  </div>
                  {openingHistoryId === h.id && <Loader2 size={13} className="animate-spin shrink-0 ml-2" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <label
        htmlFor="ai-doc-upload"
        className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-komal-blue rounded-xl py-10 cursor-pointer transition-colors"
      >
        <UploadCloud size={28} className="text-gray-400 mb-2" />
        <span className="text-sm text-gray-600">
          {file ? file.name : 'Click to choose a PDF, or drop one here'}
        </span>
        <input
          id="ai-doc-upload"
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Document type (optional — applies a standard checklist)
        </label>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
        >
          <option value="">— No checklist, general review —</option>
          {documentTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {selectedType && checklists.length > 0 && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Checklist</label>
          <select
            value={selectedChecklistId}
            onChange={(e) => setSelectedChecklistId(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
          >
            {checklists.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {selectedChecklist && !editingChecklist && (
            <div className="mt-2 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <span>{selectedChecklist.questions.length} question(s) will be asked</span>
                <button
                  onClick={() => setEditingChecklist(selectedChecklist)}
                  className="flex items-center gap-1 text-komal-blue hover:underline"
                >
                  <Pencil size={11} /> Edit
                </button>
              </div>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {selectedChecklist.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}

          {editingChecklist && (
            <ChecklistEditor checklist={editingChecklist} onClose={() => setEditingChecklist(null)} onSaved={refreshChecklists} />
          )}
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Additional questions (optional, one per line)
        </label>
        <textarea
          value={extraQuestions}
          onChange={(e) => setExtraQuestions(e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Anything specific to this document, on top of the checklist above"
        />
      </div>

      {!creatingChecklist ? (
        <button
          onClick={() => setCreatingChecklist(true)}
          className="flex items-center gap-1 text-xs font-medium text-komal-blue hover:underline"
        >
          <Plus size={12} /> New checklist for a document type
        </button>
      ) : (
        <NewChecklistForm onClose={() => setCreatingChecklist(false)} onSaved={refreshChecklists} />
      )}

      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={submit}
          disabled={!file || reviewing}
          className="bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-40 text-white text-sm font-semibold px-5 py-2.5 rounded flex items-center gap-2"
        >
          {reviewing && <Loader2 size={14} className="animate-spin" />}
          {reviewing ? `Reviewing… (${reviewElapsed}s)` : 'Review Document'}
        </button>
        <button
          onClick={submitFull}
          disabled={!file || startingFull}
          className="text-sm font-semibold text-komal-blue hover:underline disabled:opacity-40 flex items-center gap-1.5"
        >
          {startingFull && <Loader2 size={13} className="animate-spin" />}
          Review whole document instead (large files, takes a few minutes)
        </button>
      </div>

      {tooLarge && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3">
          This document is too large for a single review. Use{' '}
          <button onClick={submitFull} className="font-semibold underline">
            Review whole document instead
          </button>{' '}
          above to review it in full, in the background.
        </div>
      )}

      {result && (
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm">{result.filename}</h3>
            <span className="text-xs text-gray-500">{result.pages} page(s)</span>
          </div>
          {result.checklist_name && (
            <p className="text-xs text-komal-blue mb-2 font-medium">Checklist: {result.checklist_name}</p>
          )}
          {result.truncated && (
            <p className="text-xs text-amber-600 mb-3">
              This document was long and was truncated before review — use "Review whole document instead" above for full coverage.
            </p>
          )}
          <div className="text-sm text-gray-800 space-y-0.5">
            <MessageContent text={result.review} />
          </div>
          <button
            onClick={() => onAskInChat(result.filename, result.document_text)}
            className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-komal-blue hover:underline"
          >
            <MessageSquare size={14} /> Ask more about this in Chat
          </button>
        </div>
      )}

      {fullJob && (
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-gray-900 text-sm">{fullJob.filename}</h3>
            <span className="text-xs text-gray-500 capitalize">{fullJob.status}</span>
          </div>

          {(fullJob.status === 'pending' || fullJob.status === 'running') && (
            <div className="mb-3">
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                <div
                  className="bg-komal-blue h-1.5 rounded-full transition-all"
                  style={{ width: `${fullJob.total_chunks ? (fullJob.chunks_done / fullJob.total_chunks) * 100 : 3}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">
                {fullJob.total_chunks
                  ? `Reviewing section ${fullJob.chunks_done} of ${fullJob.total_chunks}… (about ${formatChunkEta(fullJob.total_chunks - fullJob.chunks_done)} left)`
                  : 'Starting…'}
              </p>
            </div>
          )}

          {fullJob.status === 'failed' && (
            <p className="text-sm text-red-600 mb-2">Review failed: {fullJob.error}</p>
          )}

          {fullJob.checklist_name && (
            <p className="text-xs text-komal-blue mb-2 font-medium">Checklist: {fullJob.checklist_name}</p>
          )}

          {fullJob.result?.length > 0 && (
            <div className="space-y-4">
              {fullJob.result.map((section) => (
                <div key={section.chunk} className="border-t border-gray-100 pt-3 first:border-0 first:pt-0">
                  {/* A single-request review reopened from history is 1-of-1 -
                      "Section 1 of 1" would just be noise, so it's only shown
                      for an actual multi-section chunked review. */}
                  {fullJob.total_chunks > 1 && (
                    <div className="text-xs font-semibold text-gray-500 mb-1">
                      Section {section.chunk} of {fullJob.total_chunks}
                    </div>
                  )}
                  <div className="text-sm text-gray-800 space-y-0.5">
                    <MessageContent text={section.text} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {fullJob.status === 'done' && fullJob.chat_context && (
            <button
              onClick={() => onAskInChat(fullJob.filename, fullJob.chat_context)}
              className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-komal-blue hover:underline"
            >
              <MessageSquare size={14} /> Ask more about this in Chat
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const AiAssistantPage = () => {
  const showToast = useUIStore((s) => s.showToast);
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [attachedDocument, setAttachedDocument] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Hydrates the Chat tab from the last 10 saved exchanges on first load,
  // so a browser refresh doesn't start from zero. Only visible turns are
  // saved server-side (see ai_routes.py's AiChatMessage) - a document
  // attached via onAskInChat won't survive a refresh and needs
  // re-uploading, but the surrounding conversation does.
  useEffect(() => {
    getChatHistory()
      .then((res) => {
        if (res.data.messages.length > 0) setMessages(res.data.messages);
      })
      .catch(() => {}) // non-fatal - Chat still works, just starts empty
      .finally(() => setHistoryLoaded(true));
  }, []);

  const clearHistory = async () => {
    try {
      await deleteChatHistory();
      setMessages([]);
      setAttachedDocument(null);
      showToast('Conversation cleared', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to clear conversation', 'error');
    }
  };

  // Carries a reviewed document into the Chat tab as real conversation
  // history (a hidden turn holding the full extracted text, plus the
  // review as a normal visible turn) so follow-up questions - asked
  // "later" rather than at upload time - can still reference it, without
  // re-uploading or re-extracting the PDF. Answers the "can I ask
  // questions after the fact" design question directly: yes, because it's
  // the same conversation either way.
  // The review text itself is deliberately NOT replayed into history here
  // (it was, originally) - every follow-up question resends the whole
  // history verbatim, so carrying both the raw document AND its full
  // review meant paying for the document's token cost twice on every
  // single message. Found live: review, then immediately asking one
  // follow-up, failed 100% of the time on Groq's rate limit - a short
  // fixed acknowledgment instead of the real review cuts that recurring
  // cost roughly in half without losing anything the model needs (it has
  // the raw document text either way, and can re-derive its own opinion
  // of it fresh each time rather than parroting a stale one).
  const onAskInChat = (filename, documentText) => {
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `Here is a document I uploaded: ${filename}\n\n${documentText}`, hidden: true },
      { role: 'assistant', content: `Got it — I've read ${filename}. What would you like to know?` },
    ]);
    setAttachedDocument(filename);
    setTab('chat');
  };

  const tabClasses = (key) =>
    `flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
      tab === key
        ? 'border-komal-blue text-komal-blue'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-4">Ask Komal</h1>

        <div className="glass-card overflow-hidden">
          <div className="flex border-b border-gray-200 px-3 pt-2">
            <button className={tabClasses('chat')} onClick={() => setTab('chat')}>
              <MessageSquare size={15} /> Chat
            </button>
            <button className={tabClasses('review')} onClick={() => setTab('review')}>
              <FileText size={15} /> Document Review
            </button>
          </div>

          {tab === 'chat' ? (
            <ChatTab
              messages={messages}
              setMessages={setMessages}
              attachedDocument={attachedDocument}
              setAttachedDocument={setAttachedDocument}
              historyLoaded={historyLoaded}
              onClear={clearHistory}
            />
          ) : (
            <DocumentReviewTab onAskInChat={onAskInChat} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
